import { useState, useRef } from 'react';
import { CL } from './colors.js';
import { L } from './lang.js';
import { daysFor, relativeTime, displayPlanSummary, localEodToUtc } from './format.js';
import { supabase } from './supabase.js';

const STATUS_DOT = {
  paid: CL.olive, trial: CL.gold, grace: CL.terra,
  expired: CL.stone, cancelled: CL.stone,
};

export function CustomerRow({ row, expanded, onToggle, onUpdated }) {
  const days = daysFor(row);

  return (
    <>
      <tr
        onClick={() => onToggle(row.id)}
        style={{ cursor: 'pointer', borderBottom: `1px solid ${CL.goldL}`,
                 background: expanded ? '#fdf7ee' : 'transparent' }}
      >
        <td style={{ padding: '12px 16px', verticalAlign: 'top' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
            <span style={{ color: CL.stone, fontSize: 10, lineHeight: '20px', flexShrink: 0 }}>
              {expanded ? '▾' : '▸'}
            </span>
            <div>
              <div style={{ fontWeight: 600, color: CL.bark, fontSize: 13 }}>{row.name}</div>
              <div style={{ color: CL.stone, fontSize: 12, marginTop: 2 }}>
                {row.address || row.country || '—'}
              </div>
            </div>
          </div>
        </td>
        <td style={{ padding: '12px 16px', verticalAlign: 'top' }}>
          <div style={{ color: CL.bark, fontSize: 13 }}>{row.owner_email}</div>
        </td>
        <td style={{ padding: '12px 16px', verticalAlign: 'top' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                           background: STATUS_DOT[row.plan_status] || CL.stone }} />
            <span style={{ fontSize: 13, fontWeight: 500, color: CL.bark }}>
              {row.plan_status[0].toUpperCase() + row.plan_status.slice(1)}
            </span>
          </div>
        </td>
        <td style={{ padding: '12px 16px', verticalAlign: 'top',
                     fontSize: 13, fontWeight: 600,
                     color: days != null && days <= 5 ? CL.terra : CL.bark }}>
          {days != null ? days : <span style={{ color: CL.stone }}>—</span>}
        </td>
        <td style={{ padding: '12px 16px', verticalAlign: 'top', fontSize: 12, color: CL.stone }}>
          {relativeTime(row.last_seen_at)}
        </td>
      </tr>

      {expanded && (
        <tr style={{ background: CL.sand }}>
          <td colSpan={5} style={{ padding: 0, borderBottom: `1px solid ${CL.goldL}` }}>
            <ExpandedPanel row={row} onUpdated={onUpdated} />
          </td>
        </tr>
      )}
    </>
  );
}

function ExpandedPanel({ row, onUpdated }) {
  const [pendingExtend, setPendingExtend] = useState(null);
  const [pendingPaid, setPendingPaid]     = useState(false);
  const [pendingCancel, setPendingCancel] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [customDate, setCustomDate]         = useState('');
  const [pendingCustom, setPendingCustom]   = useState(false);

  const [notes, setNotes]     = useState(row.admin_notes ?? '');
  const [saveState, setSaveState] = useState('idle');
  const [actionError, setActionError] = useState(null);
  const debounceRef = useRef(null);

  function reportActionError(err, fallbackMsg) {
    console.error('[CustomerRow]', err);
    setActionError(err?.status === 401 ? L.sessionExpired : fallbackMsg);
  }

  const showExtend = ['trial', 'grace', 'expired', 'cancelled'].includes(row.plan_status);
  const showPaid   = row.plan_status !== 'paid';
  const showCancel = ['paid', 'expired'].includes(row.plan_status);

  const todayStr = new Date().toISOString().slice(0, 10);
  const maxDateStr = new Date(Date.now() + 365 * 86400000).toISOString().slice(0, 10);
  const minDateStr = new Date(Date.now() + 86400000).toISOString().slice(0, 10);

  async function extendByDays(days) {
    if (pendingExtend !== null) return;
    setPendingExtend(days);
    setActionError(null);
    try {
      const now = new Date();
      const current = row.trial_ends_at ? new Date(row.trial_ends_at) : now;
      const base = current.getTime() > now.getTime() ? current : now;
      const newTrialEnds = new Date(base.getTime() + days * 86400000);
      const newGraceEnds = new Date(newTrialEnds.getTime() + 7 * 86400000);
      const patch = {
        trial_ends_at: newTrialEnds.toISOString(),
        grace_ends_at: newGraceEnds.toISOString(),
      };
      if (row.plan_status === 'cancelled') patch.trial_started_at = now.toISOString();
      const { error: upErr } = await supabase.from('properties').update(patch).eq('id', row.id);
      if (upErr) { reportActionError(upErr, L.extendError); return; }
      const { error: rpcErr } = await supabase.rpc('recompute_plan_status_for', { property_id_in: row.id });
      if (rpcErr) { reportActionError(rpcErr, L.extendError); return; }
      await onUpdated();
    } catch (err) {
      reportActionError(err, L.extendError);
    } finally {
      setPendingExtend(null);
    }
  }

  async function applyCustomDate() {
    if (!customDate || pendingCustom) return;
    setPendingCustom(true);
    setActionError(null);
    try {
      const newTrialEnds = localEodToUtc(customDate, row.country || 'GR');
      const newGraceEnds = new Date(new Date(newTrialEnds).getTime() + 7 * 86400000).toISOString();
      const now = new Date();
      const patch = { trial_ends_at: newTrialEnds, grace_ends_at: newGraceEnds };
      if (row.plan_status === 'cancelled') patch.trial_started_at = now.toISOString();
      const { error: upErr } = await supabase.from('properties').update(patch).eq('id', row.id);
      if (upErr) { reportActionError(upErr, L.customDateError); return; }
      const { error: rpcErr } = await supabase.rpc('recompute_plan_status_for', { property_id_in: row.id });
      if (rpcErr) { reportActionError(rpcErr, L.customDateError); return; }
      setShowDatePicker(false);
      setCustomDate('');
      await onUpdated();
    } catch (err) {
      reportActionError(err, L.customDateError);
    } finally {
      setPendingCustom(false);
    }
  }

  async function markPaid() {
    if (pendingPaid) return;
    setPendingPaid(true);
    setActionError(null);
    try {
      const { error } = await supabase.from('properties').update({
        plan_status: 'paid', trial_ends_at: null, grace_ends_at: null,
      }).eq('id', row.id);
      if (error) { reportActionError(error, L.markPaidError); return; }
      await onUpdated();
    } finally { setPendingPaid(false); }
  }

  async function markCancelled() {
    if (pendingCancel) return;
    setPendingCancel(true);
    setActionError(null);
    try {
      const { error } = await supabase.from('properties').update({ plan_status: 'cancelled' }).eq('id', row.id);
      if (error) { reportActionError(error, L.markCancelledError); return; }
      await onUpdated();
    } finally { setPendingCancel(false); }
  }

  async function persist(value) {
    setSaveState('saving');
    const { error } = await supabase.from('properties')
      .update({ admin_notes: value }).eq('id', row.id);
    if (error) {
      console.error('[CustomerRow.persist]', error);
      setSaveState(error.status === 401 ? 'expired' : 'error');
      return;
    }
    setSaveState('saved');
    setTimeout(() => setSaveState('idle'), 2000);
  }

  function onNotesChange(e) {
    const v = e.target.value;
    setNotes(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => persist(v), 800);
  }

  function onNotesBlur() {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    persist(notes);
  }

  const joinedDate = row.created_at ? row.created_at.slice(0, 10) : '—';

  const btnStyle = (pending) => ({
    background: CL.cream, border: `1px solid ${CL.goldL}`, color: CL.bark,
    padding: '6px 13px', borderRadius: 5, fontSize: 12, fontWeight: 500,
    fontFamily: 'inherit', cursor: pending ? 'not-allowed' : 'pointer',
    opacity: pending ? 0.6 : 1,
  });

  return (
    <div style={{ padding: '14px 16px 14px 36px' }}>
      <div style={{ fontSize: 12, color: CL.stone, fontWeight: 500, marginBottom: 12 }}>
        {displayPlanSummary(row)}
      </div>

      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 20, flexWrap: 'wrap',
                    marginBottom: 12 }}>
        {(showExtend || showDatePicker) && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            {showExtend && (
              <>
                <button style={btnStyle(pendingExtend === 7)}
                  disabled={pendingExtend !== null}
                  onClick={() => extendByDays(7)}>
                  {L.btnPlus7}
                </button>
                <button style={btnStyle(pendingExtend === 30)}
                  disabled={pendingExtend !== null}
                  onClick={() => extendByDays(30)}>
                  {L.btnPlus30}
                </button>
                <button style={btnStyle(false)}
                  onClick={() => setShowDatePicker(v => !v)}>
                  {L.btnCustomDate}
                </button>
              </>
            )}
            {showDatePicker && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <input type="date" value={customDate} min={minDateStr} max={maxDateStr}
                  onChange={e => setCustomDate(e.target.value)}
                  style={{ border: `1px solid ${CL.goldL}`, borderRadius: 5, padding: '5px 8px',
                           fontSize: 12, fontFamily: 'inherit', background: CL.cream }} />
                <button disabled={!customDate || pendingCustom}
                  onClick={applyCustomDate}
                  style={{ ...btnStyle(pendingCustom || !customDate),
                           background: customDate && !pendingCustom ? CL.bark : undefined,
                           color: customDate && !pendingCustom ? CL.cream : undefined,
                           borderColor: customDate && !pendingCustom ? CL.bark : undefined }}>
                  Set
                </button>
              </div>
            )}
            {showPaid && (
              <button style={{ ...btnStyle(pendingPaid), background: CL.olive,
                               borderColor: CL.olive, color: '#fff' }}
                disabled={pendingPaid || pendingExtend !== null}
                onClick={markPaid}>
                {L.btnMarkPaid}
              </button>
            )}
            {showCancel && (
              <button style={{ ...btnStyle(pendingCancel), background: CL.terra,
                               borderColor: CL.terra, color: '#fff' }}
                disabled={pendingCancel}
                onClick={markCancelled}>
                {L.btnMarkCancelled}
              </button>
            )}
          </div>
        )}
        {!showExtend && showPaid && (
          <button style={{ ...btnStyle(pendingPaid), background: CL.olive,
                           borderColor: CL.olive, color: '#fff' }}
            disabled={pendingPaid}
            onClick={markPaid}>
            {L.btnMarkPaid}
          </button>
        )}
        {!showExtend && showCancel && (
          <button style={{ ...btnStyle(pendingCancel), background: CL.terra,
                           borderColor: CL.terra, color: '#fff' }}
            disabled={pendingCancel}
            onClick={markCancelled}>
            {L.btnMarkCancelled}
          </button>
        )}

        <div style={{ flex: 1, minWidth: 280 }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em',
                        textTransform: 'uppercase', color: CL.stone, marginBottom: 5 }}>
            {L.notesLabel}
          </div>
          <textarea
            value={notes}
            onChange={onNotesChange}
            onBlur={onNotesBlur}
            rows={3}
            style={{ width: '100%', border: `1px solid ${CL.goldL}`, borderRadius: 5,
                     padding: '7px 10px', fontSize: 13, fontFamily: 'inherit',
                     background: CL.cream, color: CL.bark, resize: 'vertical' }}
          />
          {saveState === 'saved' && (
            <div style={{ fontSize: 11, color: CL.olive, marginTop: 3 }}>{L.notesSaved}</div>
          )}
          {saveState === 'expired' && (
            <div style={{ fontSize: 11, color: CL.terra, marginTop: 3 }}>{L.notesExpired}</div>
          )}
          {saveState === 'error' && (
            <div style={{ fontSize: 11, color: CL.terra, marginTop: 3 }}>{L.notesSaveError}</div>
          )}
        </div>
      </div>

      {actionError && (
        <div style={{ fontSize: 12, color: CL.terra, marginBottom: 10,
                      background: '#FCEFE7', border: `1px solid ${CL.terra}`,
                      borderRadius: 5, padding: '6px 10px' }}>
          {actionError}
        </div>
      )}

      <div style={{ fontSize: 12, color: CL.stone }}>
        {row.room_count} rooms · {row.booking_count} bookings · joined {joinedDate}
      </div>
    </div>
  );
}
